import { Dispatch, SetStateAction, useCallback } from 'react';
import { isEmpty } from 'lodash';
import { NoteData } from 'components/Notes/types';
import { getStringValue } from 'utilities';
import { Note } from '../../types';

interface SaveNotesOptions {
  onSuccess?: (savedNotes: NoteData[]) => void;
  onSettled?: () => void;
}

interface SavePanelNotesEntriesParams {
  canSavePanel?: (panelId: string) => boolean;
  notes: Note[];
  notesSaveInFlightByPanelRef: { current: Set<string> };
  saveNotes: (notesData?: NoteData[], options?: SaveNotesOptions) => void;
  setNotes: Dispatch<SetStateAction<Note[]>>;
}

// Plain (non-hook) version so callers that must run this per item in a loop (e.g. one
// approach at a time) can call it directly instead of calling a hook conditionally/in a loop.
export const savePanelNotesEntries = (params: SavePanelNotesEntriesParams) => {
  const { canSavePanel, notes, notesSaveInFlightByPanelRef, saveNotes, setNotes } = params;

  if (isEmpty(notes)) {
    return;
  }

  notes.forEach(noteEntry => {
    const panelId = getStringValue(noteEntry?.panelId);

    if (
      noteEntry?.notes &&
      !isEmpty(noteEntry?.notes) &&
      noteEntry?.notesHasChanged &&
      !notesSaveInFlightByPanelRef.current.has(panelId) &&
      (!canSavePanel || canSavePanel(panelId))
    ) {
      notesSaveInFlightByPanelRef.current.add(panelId);

      saveNotes(noteEntry?.notes, {
        onSuccess: savedNotes => {
          setNotes(previousNotes =>
            previousNotes.map(currentNote =>
              currentNote.panelId === noteEntry.panelId
                ? {
                  ...currentNote,
                  notes: savedNotes,
                  notesHasChanged: false,
                }
                : currentNote
            )
          );
        },
        onSettled: () => {
          notesSaveInFlightByPanelRef.current.delete(panelId);
        },
      });
    }
  });
};

type UseSavePanelNotesParams = SavePanelNotesEntriesParams;

// Shared by panel-note hooks (e.g. calibration, range sensitivity) that keep their own
// note list state but save/update entries the same way.
export const useSavePanelNotes = (params: UseSavePanelNotesParams) => {
  const { canSavePanel, notes, notesSaveInFlightByPanelRef, saveNotes, setNotes } = params;

  const savePanelNotes = useCallback(() => {
    savePanelNotesEntries({ canSavePanel, notes, notesSaveInFlightByPanelRef, saveNotes, setNotes });
  }, [canSavePanel, notes, notesSaveInFlightByPanelRef, saveNotes, setNotes]);

  return { savePanelNotes };
};
